import React, { useState, useEffect, useMemo } from 'react'
import * as R from 'ramda'
import moment from 'moment'
import { useInvoiceAPI } from './api'
import { InvoiceOrdersResponse, InvoiceOrder } from './model'
import PaginationUI from 'react-js-pagination'
import { formatCents as formatCentsSimple } from './utils'
import { Panel } from '../order/v5/fragments/comp/panel'

import { useRouteMatch } from 'react-router'
import { InvoiceableInfo, InvoiceableTransactions, NonOrderInvoiceableTransactions } from './order-transactions-v1'

export default function OrdersTransactions(props: {
  invoiceId: number
  invoiceType: string
  simplifiedInvoice: boolean
}): JSX.Element {
  const invoiceAPI = useInvoiceAPI()
  const [orders, setOrders] = useState<InvoiceOrdersResponse | null>(null)
  const [page, setPage] = useState(1)

  const pageSize = 25

  useEffect(() => {
    invoiceAPI.getInvoiceOrders(props.invoiceId as number, page, pageSize).then(setOrders)
  }, [props.invoiceId, page])

  const extraHeaders = useMemo<string[]>(() => {
    if (orders) {
      const positiveColumns = Array.from(
        orders.orders.reduce(
          (s: Set<string>, o: InvoiceOrder) =>
            R.keys(o.rules_info).reduce(
              (s: Set<string>, h: string) => (o.rules_info[h].totals.total > 0 ? s.add(h) : s),
              s
            ),
          new Set<string>()
        )
      ).sort()
      const negativeColumns = Array.from(
        orders.orders.reduce(
          (s: Set<string>, o: InvoiceOrder) =>
            R.keys(o.rules_info).reduce(
              (s: Set<string>, h: string) => (o.rules_info[h].totals.total < 0 ? s.add(h) : s),
              s
            ),
          new Set<string>()
        )
      ).sort()
      return R.uniq([...positiveColumns, ...negativeColumns])
    } else {
      return []
    }
  }, [orders])

  if (!orders) return <>Loading...</>

  return (
    <>
      <Panel id="orders" label="Orders">
        <table className="table table-condensed table-striped">
          <thead>
            <tr>
              <th>Id</th>
              <th>Order Type</th>
              {!props.simplifiedInvoice && <th>Order Total</th>}
              <th>Customer Name</th>
              <th>Date</th>
              <th>Tender</th>
              <th>Order Source</th>
              {!props.simplifiedInvoice && extraHeaders.map((h: string) => <th key={h}>{h}</th>)}
              <th>Total Due</th>
            </tr>
          </thead>
          <tbody>
            {orders.orders.map((o: InvoiceOrder) => (
              <InvoiceOrderLine
                key={o.order_uuid}
                invoiceId={props.invoiceId}
                invoiceType={props.invoiceType}
                order={o}
                extraHeaders={extraHeaders}
                simplifiedInvoice={props.simplifiedInvoice}
              />
            ))}
          </tbody>
        </table>
        <PaginationUI
          prevPageText="prev"
          nextPageText="next"
          firstPageText="first"
          lastPageText="last"
          itemsCountPerPage={pageSize}
          activePage={orders.pagination.page}
          pageRangeDisplayed={3}
          onChange={setPage}
          totalItemsCount={orders.pagination.total}
          itemClass="page-item"
          linkClass="page-link"
        />
      </Panel>
      <NonOrderInvoiceableTransactions {...props} />
    </>
  )
}
interface InvoiceOrderLineProps {
  invoiceId: number
  invoiceType: string
  order: InvoiceOrder
  extraHeaders: string[]
  simplifiedInvoice: boolean
}

const formatCents = (nr: number): JSX.Element => {
  if (nr < 0) {
    return <span className="text-danger">{formatCentsSimple(nr)}</span>
  } else {
    return <span className="text-success">{formatCentsSimple(nr)}</span>
  }
}

function InvoiceOrderLine(props: InvoiceOrderLineProps): JSX.Element {
  const { order } = props
  const match = useRouteMatch<{ invoiceId: string; invoiceType: string; orderUUID: string }>()
  const totalWithConvFee = order.order_total + (order.convenience_fee || 0)
  // console.log('Route params InvoiceOrderLine', match)
  // <td>{order.disbursment_dates.map((d) => moment(d).format('L')).join(', ')}</td>

  return (
    <>
      <tr>
        <td>
          <a href={`#/orders/${order.order_uuid}`} target="_blank" rel="noreferrer">
            {order.order_uuid.split(/-/)[0]}
          </a>
        </td>
        <td>
          {props.simplifiedInvoice ? (
            <>{order.order_type}</>
          ) : (
            <a
              href={
                match.params.orderUUID && match.params.orderUUID == order.order_uuid
                  ? `#/statements/${props.invoiceType}/${props.invoiceId}`
                  : `#/statements/${props.invoiceType}/${props.invoiceId}/order/${order.order_uuid}/transactions`
              }>
              {order.order_type}
            </a>
          )}
        </td>
        {!props.simplifiedInvoice && <td align="right">{formatCents(totalWithConvFee)}</td>}
        <td>{order.customer_name}</td>
        <td>{moment(order.order_date).format('L')}</td>
        <td>{order.payment_tender}</td>
        <td>{order.order_source}</td>
        {!props.simplifiedInvoice &&
          props.extraHeaders.map((h: string) => (
            <td key={h} align="right">
              {order.rules_info[h] && formatCents(order.rules_info[h].totals.total)}
            </td>
          ))}
        <td align="right">{formatCents(computeTotalsDue(order))}</td>
      </tr>
      {!!match.params.orderUUID && match.params.orderUUID == order.order_uuid && (
        <OrderTransactions {...props} extraHeadersCount={props.extraHeaders.length} />
      )}
    </>
  )
}

function computeTotalsDue(o: InvoiceOrder): number {
  return R.values(o.rules_info).reduce((grandTotal, { totals: { total } }) => grandTotal + total, 0)
}

function OrderTransactions(props: InvoiceOrderLineProps & { extraHeadersCount: number }): JSX.Element {
  return (
    <tr>
      <td colSpan={10 + props.extraHeadersCount}>
        <InvoiceableInfo
          billable_id={props.order.order_uuid}
          billable_type={'order'}
          count={1}
          invoiceId={props.invoiceId}
        />
      </td>
    </tr>
  )
}
